import { JSX, useEffect, useMemo } from 'react';
import {
  CheckCircle as CompletedIcon,
  Cancel as FailedIcon,
  WorkHistory as InProgressIcon,
  NotStarted as NotStartedIcon,
  Pending as PendingIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { StyledInputAdornment } from 'layouts/Main/components/BulkActionsLedger/components/BulkActionsToolbar/BulkActionsToolbar';
import { ReportStatusRow } from 'layouts/Main/components/ExcelExportDialogV2/FirmExcelExportDialog/components';
import { normalizeFilterInput } from 'utilities';
import { ReportStatusesDisplayProps } from './ReportStatusesDisplay.props';
import {
  COMPLETED_VALUE,
  FAILED_VALUE,
  IN_PROGRESS_VALUE,
  NOT_STARTED_VALUE,
  WAITING_PICK_UP_VALUE,
} from '../../../constants';

const statusIconMap: Record<number, JSX.Element> = {
  [IN_PROGRESS_VALUE]: <InProgressIcon color="primary" />,
  [COMPLETED_VALUE]: <CompletedIcon color="success" />,
  [FAILED_VALUE]: <FailedIcon color="error" />,
  [WAITING_PICK_UP_VALUE]: <PendingIcon color="warning" />,
  [NOT_STARTED_VALUE]: <NotStartedIcon color="disabled" />,
};

const ReportStatusesDisplay = ({
  reportStatuses,
  companySearchString,
  setCompanySearchString,
  handleGlobalToggle, // for zip job
  handleIndividualChange, // for zip job
  reportsToRequest,
  handleEnqueueCheckboxChange,
  enqueueToTrigger,
  setEnqueueToTrigger,
  enqueueButtonHandler,
  enqueueButtonHandlerIsDisabled,
  displayDateTooltip,
}: ReportStatusesDisplayProps) => {
  const visibleReportStatuses = useMemo(() => {
    if (!reportStatuses) {
      return [];
    }
    if (!companySearchString) {
      return reportStatuses;
    }
    return reportStatuses.filter(rs => (rs.company_name?.toLowerCase() ?? '').includes(companySearchString));
  }, [reportStatuses, companySearchString]);

  // meant to run only once, on component mount
  // reportStatuses is guaranteed to be non-null here because the parent component
  // only renders this component when reportStatuses is non-null
  useEffect(() => {
    setEnqueueToTrigger(
      new Set(reportStatuses.filter(rs => rs.status === NOT_STARTED_VALUE).map(rs => rs.company_measurement_date))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Paper elevation={1} sx={{ width: 'calc(100% - 25px)', marginLeft: '25px' }}>
        <div style={{ margin: '1em auto', paddingTop: '1em' }}>
          <TextField
            variant="standard"
            placeholder="Find a company"
            fullWidth
            value={companySearchString}
            onChange={e => setCompanySearchString(normalizeFilterInput(e.target.value))}
            slotProps={{
              input: {
                startAdornment: (
                  <StyledInputAdornment position="start">
                    <SearchIcon />
                  </StyledInputAdornment>
                ),
              },
            }}
            style={{ marginRight: 16 }}
          />
        </div>
      </Paper>
      <Paper elevation={2} sx={{ width: 'calc(100% - 25px)', marginLeft: '25px' }}>
        <TableContainer>
          <FormGroup>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={1}>
                    <FormControlLabel
                      control={<Checkbox onChange={handleGlobalToggle} name="all" />}
                      label="Select All"
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ paddingRight: '15em' }} colSpan={2}>
                    <Button disabled={enqueueButtonHandlerIsDisabled} onClick={enqueueButtonHandler}>
                      START EXPORTS
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: 1 / 3 }}>COMPANY</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 1 / 3 }}>REPORT STATUS</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 1 / 3 }}>EXPORTS TO CREATE</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleReportStatuses.map(reportStatus => (
                  <ReportStatusRow
                    key={reportStatus.company_measurement_date}
                    reportStatus={reportStatus}
                    icon={statusIconMap[reportStatus.status!]}
                    handleIndividualChange={handleIndividualChange}
                    handleEnqueueCheckboxChange={handleEnqueueCheckboxChange}
                    checkedForZipJob={reportsToRequest[reportStatus.company_measurement_date]}
                    checkedForEnqueue={enqueueToTrigger.has(reportStatus.company_measurement_date)}
                    displayDateTooltip={displayDateTooltip}
                  />
                ))}
              </TableBody>
            </Table>
          </FormGroup>
        </TableContainer>
      </Paper>
    </>
  );
};

export default ReportStatusesDisplay;
